import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './GoogleCharts.scss';

const DEBOUNCE_RESIZE_THRESOLD = 200;

const ARRAY_DATA_TYPE = 'Array';
// const FULL_OBJECTS_DATA_TYPE = 'Full';

const Palettes = {
  // https://www.displayr.com/choosing-color-palettes-in-displayr/
  colorBlind: ['#65C2A5', '#FB8D61', '#E78AC3', '#E78AC3', '#A6D853', '#FBD930', '#E4C493', '#B3B3B3'],
  lightPastels: ['#8DD3C8', '#FDFFB3', '#BEBBDA', '#FB8071', '#80B1D2', '#FBB262', '#B3DE6A', '#FDCDE4', '#D9D9D9', '#BC80BC', '#CCEAC4', '#FBED6F'],
  strongColors: ['#E51E1D', '#377DB8', '#4DAF4A', '#984FA1', '#FA7E01', '#FBFF32', '#A65629', '#F781BE', '#999999'],
  // https://experience.sap.com/fiori-design-web-versions-1-26-and-1-28/ui-components/color-palettes/ui-theme-designer-for-color-palettes/
  SAP: ['#5BBAE6', '#B5D958', '#FAC264', '#8CD3FF', '#D998CB', '#F1D248', '#94B9C6', '#CCC5A8', '#53BACC', '#DBDB46', '#98AAFB'],
};

const ChartPackages = {
  AnnotationChart: ['corechart', 'annotationchart'],
  AnnotatedTimeLine: ['corechart', 'annotatedtimeline'],
  PieChart: ['corechart'],
  ScatterChart: ['corechart'],
  LineChart: ['corechart'],
  SteppedAreaChart: ['corechart'],
  Histogram: ['corechart'],
  ColumnChart: ['corechart', 'bar'],
  Timeline: ['corechart', 'timeline'],
};

class GoogleChart extends React.PureComponent {
  constructor(props) {
    super(props);

    this.domWrapperRef = React.createRef();
    this.domRef = React.createRef();
    const { width, height, className, chartType, dataType, columns, palette, ...options } = props.configuration;
    this.chartType = chartType;
    this.width = width;
    this.height = height;
    if (width || height) {
      this.style = {};
      if (width) {
        this.style.width = width;
      }
      if (height) {
        this.style.height = height;
      }
    }
    this.dataType = dataType || ARRAY_DATA_TYPE;
    this.columns = columns;
    this.options = options;
    options.width = '100%';
    if (palette) {
      if (Array.isArray(palette)) {
        this.options.colors = palette;
      } else if (Palettes[palette]) {
        this.options.colors = Palettes[palette];
      }
    }
    this.className = className;
    this.googleChartsInitialized = false;

    this.drawChart = this.drawChart.bind(this);
    this.updateSizes = this.updateSizes.bind(this);
  }

  async componentDidMount() {
    window.addEventListener('resize', this.updateSizes);
    const firstdrawChart = () => {
      if (!this.unmounted) {
        this.googleChartsInitialized = true;
        this.drawChart();
      }
    };
    const {
      GoogleCharts,
    } = await import(/* webpackChunkName: "googlecharts" */ 'google-charts');
    this.GoogleCharts = GoogleCharts;
    GoogleCharts.load(firstdrawChart, {
      packages: ChartPackages[this.chartType],
    });
  }

  componentDidUpdate(prevProps) {
    if ((prevProps.data !== this.props.data) && this.googleChartsInitialized) {
      this.drawChart();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  updateSizes() {
    if (this.domRef.current && this.googleChartsInitialized) {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.timeout = null;
        this.drawChart();
      }, DEBOUNCE_RESIZE_THRESOLD);
    }
  }

  drawChart() {
    const { data } = this.props;
    if (!this.GoogleCharts) {
      // pending draw triggered between a unmounting/mounting
      return;
    }
    let chartData;
    if (this.columns) {
      // Constructs a litteral DataTable initializer
      const objectToRow = (o) => {
        const cells = this.columns.map((col) => {
          const cell = { v: col.getValue ? col.getValue(o) : o[col.name] };
          if (col.formatValue) {
            cell.f = col.formatValue(cell.v, o, col.name, col);
          } else {
            const editor = this.props.viewService && this.props.viewService.getEditor(col.type);
            if (editor) {
              cell.f = col.editor.formatValue(cell.v, data, col.name, col);
            }
          }
          return cell;
        });
        return { c: cells };
      };

      const rowMapper = this.dataType === ARRAY_DATA_TYPE
        ? row => ({ c: row.map(c => (c && (c.v !== undefined) ? c : { v: c })) })
        : o => ({ c: objectToRow(o) });
      const rows = data.map(rowMapper);
      chartData = new this.GoogleCharts.api.visualization.DataTable({
        cols: this.columns.map(col => ({ type: col.type, id: col.id || col.name, label: col.label, role: col.role })),
        rows,
      });
    } else {
      chartData = this.GoogleCharts.api.visualization.arrayToDataTable(data);
    }
    const chart = new this.GoogleCharts.api.visualization[this.chartType](this.domRef.current);
    chart.draw(chartData, this.options);
  }

  render() {
    return (
      <div
        className={classnames('grb-google-chart-wrapper', this.className)}
        style={this.style}
        ref={this.domWrapperRef}
      >
        <div className="grb-google-chart" ref={this.domRef} />
      </div>
    );
  }
}

GoogleChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ]),
    ),
  ),
  configuration: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    dataType: PropTypes.string,
    className: PropTypes.string,
    width: PropTypes.string,
    height: PropTypes.string,
    chartType: PropTypes.string,
    palette: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
    ]),
  }),
  viewService: PropTypes.shape({
    getEditor: PropTypes.func,
  }),
};

GoogleChart.defaultProps = {
  data: null,
  configuration: {},
  viewService: undefined,
};

const DefaultChartColors = Palettes.SAP;

export { GoogleChart as default, Palettes, DefaultChartColors };
